import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { subscriptionsAPI, platformAPI, fileUrl, authAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  Building2, User, Mail, Lock, ArrowRight, ArrowLeft, CheckCircle,
  Eye, EyeOff, Shield, BarChart3, Zap, Users, Phone, MessageSquare, RefreshCw,
} from 'lucide-react';
import { firebaseEnabled, sendPhoneOtp, confirmPhoneOtp, signInWithGoogle } from '../../services/firebase';

const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(v || 0);

const COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Argentina', 'Australia', 'Austria',
  'Bangladesh', 'Belgium', 'Brazil', 'Canada', 'China', 'Colombia',
  'Denmark', 'Egypt', 'Finland', 'France', 'Germany', 'Ghana',
  'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy',
  'Japan', 'Jordan', 'Kenya', 'Korea', 'Kuwait', 'Lebanon', 'Libya',
  'Malaysia', 'Mexico', 'Morocco', 'Netherlands', 'New Zealand', 'Nigeria',
  'Norway', 'Pakistan', 'Palestine', 'Philippines', 'Qatar', 'Russia',
  'Saudi Arabia', 'South Africa', 'Spain', 'Sweden', 'Switzerland',
  'Turkey', 'UAE', 'Uganda', 'Ukraine', 'United Kingdom', 'United States',
  'Uzbekistan', 'Vietnam', 'Yemen',
];

const CURRENCIES = [
  { code: 'USD', label: 'USD — US Dollar' },
  { code: 'AFN', label: 'AFN — Afghan Afghani' },
  { code: 'EUR', label: 'EUR — Euro' },
  { code: 'GBP', label: 'GBP — British Pound' },
  { code: 'CAD', label: 'CAD — Canadian Dollar' },
  { code: 'AUD', label: 'AUD — Australian Dollar' },
  { code: 'SAR', label: 'SAR — Saudi Riyal' },
  { code: 'AED', label: 'AED — UAE Dirham' },
  { code: 'PKR', label: 'PKR — Pakistani Rupee' },
  { code: 'INR', label: 'INR — Indian Rupee' },
];

function getPasswordStrength(pw) {
  if (!pw) return { level: 0, label: '', class: '' };
  let score = 0;
  if (pw.length >= 6) score++;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 1) return { level: 1, label: 'Weak', class: 'weak' };
  if (score <= 2) return { level: 2, label: 'Fair', class: 'fair' };
  if (score <= 3) return { level: 3, label: 'Good', class: 'good' };
  return { level: 4, label: 'Strong', class: 'strong' };
}

export default function Register() {
  const { register, googleRegister } = useAuth();
  const [searchParams] = useSearchParams();
  const planIdFromUrl = searchParams.get('plan');
  const [step, setStep] = useState(1);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [showPw, setShowPw] = useState(false);
  const [showPwConfirm, setShowPwConfirm] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [errors, setErrors] = useState({});
  const [platformLogo, setPlatformLogo] = useState(null);
  const [otpPhone, setOtpPhone] = useState('+93');
  const [otpCode, setOtpCode] = useState('');
  const [otpStage, setOtpStage] = useState('phone'); // 'phone' | 'code'
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpIdToken, setOtpIdToken] = useState(null);
  const [confirmResult, setConfirmResult] = useState(null);
  const [otpBusy, setOtpBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [emailCheck, setEmailCheck] = useState({ status: 'idle', msg: '' }); // idle|checking|valid|invalid
  const [form, setForm] = useState({
    companyName: '', companyType: '', country: 'Afghanistan',
    currency: 'AFN', email: '', phone: '', password: '', confirmPassword: '',
    username: '', firstName: '', lastName: '',
  });

  // Live email verification — blocks dummy / disposable / non-existent domains
  useEffect(() => {
    const val = (form.email || '').trim();
    if (!val) { setEmailCheck({ status: 'idle', msg: '' }); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(val)) {
      setEmailCheck({ status: 'invalid', msg: 'Invalid email format' });
      return;
    }
    let cancelled = false;
    setEmailCheck({ status: 'checking', msg: '' });
    const t = setTimeout(async () => {
      try {
        const res = await authAPI.checkEmail(val);
        if (cancelled) return;
        setEmailCheck(res.valid
          ? { status: 'valid', msg: '' }
          : { status: 'invalid', msg: res.reason || 'This email cannot be verified' });
      } catch {
        if (!cancelled) setEmailCheck({ status: 'invalid', msg: 'Could not verify this email. Please try again.' });
      }
    }, 600);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.email]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Public endpoint — registration visitors are not authenticated yet
    subscriptionsAPI.plansPublic().then(p => {
      const active = p.filter(pl => pl.is_active !== 0 && pl.is_active !== false);
      setPlans(active);
      if (planIdFromUrl) {
        const matched = active.find(pl => String(pl.id) === String(planIdFromUrl));
        if (matched) setSelectedPlan(matched);
        else if (active.length > 0) setSelectedPlan(active[0]);
      } else if (active.length > 0) {
        setSelectedPlan(active[0]);
      }
    }).catch(() => {});
    platformAPI.getPublicSettings().then(data => {
      if (data.logo_path) setPlatformLogo(data.logo_path);
    }).catch(() => {});

    // Prefill from query params (e.g. after Google "no account" redirect)
    const qEmail = searchParams.get('email');
    const qName = searchParams.get('name');
    if (qEmail || qName) {
      setForm(f => ({
        ...f,
        email: qEmail || f.email,
        firstName: f.firstName || (qName ? qName.split(' ')[0] : ''),
        lastName: f.lastName || (qName ? qName.split(' ').slice(1).join(' ') : ''),
      }));
    }
  }, []);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  // Validate without touching error state (used for tab styling)
  const validateStep1Silent = () => {
    return !!(
      form.companyName.trim() &&
      emailCheck.status === 'valid' &&
      form.username.trim() &&
      form.password.length >= 6 &&
      form.password === form.confirmPassword &&
      agreed
    );
  };

  // ---------- Sign up with Google (one click → blank company + trial) ----------
  const handleGoogleSignup = async () => {
    setGoogleBusy(true);
    try {
      const g = await signInWithGoogle();
      if (!g?.email) throw new Error('Google account returned no email');
      const data = await googleRegister({ email: g.email, uid: g.uid, name: g.name, idToken: g.idToken });
      toast.success(data.message || 'Account created! Your 14-day free trial has started.', { duration: 6000 });
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.message || 'Google sign-up failed');
    } finally {
      setGoogleBusy(false);
    }
  };

  // ---------- Phone OTP ----------
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const handleSendOtp = async () => {
    if (!/^\+[1-9]\d{7,14}$/.test(otpPhone.trim())) {
      toast.error('Enter a valid phone number in international format, e.g. +93700123456');
      return;
    }
    setOtpBusy(true);
    try {
      const confirmation = await sendPhoneOtp(otpPhone.trim());
      setConfirmResult(confirmation);
      setOtpStage('code');
      setResendIn(45);
      toast.success(`Verification code sent to ${otpPhone.trim()}`);
    } catch (err) {
      toast.error(err.message?.replace('Firebase:', '').trim() || 'Could not send the code.');
    } finally {
      setOtpBusy(false);
    }
  };

  const handleVerifyRegisterOtp = async () => {
    if (!/^\d{6}$/.test(otpCode.trim())) {
      toast.error('Enter the 6-digit code from the SMS');
      return;
    }
    setOtpBusy(true);
    try {
      const { phoneNumber, uid, idToken } = await confirmPhoneOtp(confirmResult, otpCode.trim());
      setOtpVerified(true);
      setOtpIdToken(idToken);
      toast.success('Phone verified!');
    } catch (err) {
      toast.error(err.message || 'Verification failed');
    } finally {
      setOtpBusy(false);
    }
  };

  const validateStep1 = () => {
    const errs = {};
    if (!form.companyName.trim()) errs.companyName = 'Company name is required';
    if (!form.email.trim()) {
      errs.email = 'Email is required';
    } else if (emailCheck.status === 'invalid') {
      errs.email = emailCheck.msg || 'This email cannot be verified';
    } else if (emailCheck.status !== 'valid') {
      errs.email = 'Verifying your email — please wait a moment';
    }
    if (!form.username.trim()) errs.username = 'Username is required';
    if (!form.password) errs.password = 'Password is required';
    else if (form.password.length < 6) errs.password = 'Password must be at least 6 characters';
    if (form.password !== form.confirmPassword) errs.confirmPassword = 'Passwords do not match';
    if (!agreed) errs.agreed = 'You must agree to the terms';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleCompanySubmit = (e) => {
    e.preventDefault();
    if (validateStep1()) setStep(2);
  };

  const handlePlanSubmit = async () => {
    if (!selectedPlan) {
      setErrors({ ...errors, plan: 'You must select a plan to create your company' });
      toast.error('Please select a plan to continue');
      return;
    }
    setLoading(true);
    try {
      const data = {
        ...form,
        selectedPlanId: selectedPlan.id,
        billingCycle,
        phone: otpVerified ? otpPhone.trim() : null,
        phoneIdToken: otpVerified ? otpIdToken : null,
      };
      const result = await register(data);
      toast.success(result.message || 'Company created! Your 14-day free trial has started.');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const pwStrength = getPasswordStrength(form.password);

  return (
    <div className="auth-layout" style={{ justifyContent: 'center', background: 'linear-gradient(135deg, #eef2ff 0%, #fdf2f8 100%)' }}>
      {/* ========== FULL-PAGE FORM — single centered column ========== */}
      <div className="auth-form-panel" style={{ maxWidth: 620 }}>
        <div className="auth-form-wrapper">
          {/* Tabs */}
          <div className="auth-steps">
            <button type="button" onClick={() => setStep(1)}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
              className={`auth-step ${step >= 1 ? 'active' : 'inactive'} ${step > 1 ? 'completed' : ''}`}>
              <div className="auth-step-dot">{step > 1 ? '✓' : '1'}</div>
              <span className="auth-step-label">Company</span>
            </button>
            <div className={`auth-step-line ${step > 1 ? 'completed' : ''}`} />
            <button type="button"
              onClick={() => { if (step === 1 && !validateStep1()) return; setStep(2); }}
              style={{ background: 'none', border: 'none', padding: 0, cursor: step === 1 && !validateStep1Silent() ? 'not-allowed' : 'pointer', opacity: step === 1 && !validateStep1Silent() ? 0.6 : 1 }}
              className={`auth-step ${step >= 2 ? 'active' : 'inactive'}`}>
              <div className="auth-step-dot">2</div>
              <span className="auth-step-label">Plan</span>
            </button>
          </div>

          <div className="auth-card">
            {/* ===== STEP 1: COMPANY INFO ===== */}
            {step === 1 && (
              <>
                <div className="auth-card-header">
                  <div className="auth-form-logo">
                    {platformLogo ? (
                      <img src={fileUrl(platformLogo)} alt="Platform Logo" className="auth-form-logo-img" style={{ objectFit: 'contain' }} />
                    ) : (
                      <img src="/favicon.svg" alt="LedgerPro" className="auth-form-logo-img" />
                    )}
                  </div>
                  <h2>Create your company</h2>
                  <p>Set up your business and start managing your finances with Ledger Pro.</p>
                </div>
                <div className="auth-card-body">
                  {firebaseEnabled && (
                    <>
                      <button type="button" onClick={handleGoogleSignup} disabled={googleBusy}
                        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9, padding: '11px 14px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', fontWeight: 600, fontSize: 13.5, cursor: googleBusy ? 'default' : 'pointer' }}>
                        {googleBusy ? (
                          <><span className="spinner" style={{ width: 15, height: 15, borderWidth: 2 }} /> Creating your company...</>
                        ) : (
                          <>
                            <svg width="17" height="17" viewBox="0 0 48 48" aria-hidden="true">
                              <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.4 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6.1C12.3 13.4 17.7 9.5 24 9.5z"/>
                              <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4.1 7.1-10.1 7.1-17.5z"/>
                              <path fill="#FBBC05" d="M10.4 28.7a14.5 14.5 0 0 1 0-9.4l-7.8-6.1a23.9 23.9 0 0 0 0 21.6l7.8-6.1z"/>
                              <path fill="#34A853" d="M24 48c6.2 0 11.4-2 15.4-5.5l-7.5-5.8c-2.1 1.4-4.8 2.3-7.9 2.3-6.3 0-11.7-3.9-13.6-9.3l-7.8 6.1C6.5 42.6 14.6 48 24 48z"/>
                            </svg>
                            Sign up with Google
                          </>
                        )}
                      </button>
                      <div className="auth-divider" style={{ margin: '16px 0' }}><span>or create with email</span></div>
                    </>
                  )}
                  {errors.agreed && <div className="auth-error">⚠ {errors.agreed}</div>}

                  <form onSubmit={handleCompanySubmit}>
                    <div className="auth-field">
                      <label>Company Name <span className="req">*</span></label>
                      <div className="auth-input-icon">
                        <Building2 size={18} className="auth-input-icon-svg" />
                        <input className={`auth-input ${errors.companyName ? 'error' : ''}`} value={form.companyName} onChange={set('companyName')} placeholder="Enter your company name" autoFocus />
                      </div>
                      {errors.companyName && <div className="auth-field-error">{errors.companyName}</div>}
                    </div>

                    <div className="auth-row">
                      <div className="auth-field">
                        <label>Business Type</label>
                        <select className="auth-input" value={form.companyType} onChange={set('companyType')}>
                          <option value="">Select business type</option>
                          <option value="sole_proprietorship">Sole Proprietorship</option>
                          <option value="llc">LLC</option>
                          <option value="corporation">Corporation</option>
                          <option value="partnership">Partnership</option>
                          <option value="nonprofit">Non-Profit</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div className="auth-field">
                        <label>Country</label>
                        <select className="auth-input" value={form.country} onChange={set('country')}>
                          {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    </div>

                    <div className="auth-field">
                      <label>Base Currency</label>
                      <select className="auth-input" value={form.currency} onChange={set('currency')}>
                        {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                      </select>
                    </div>

                    <div className="auth-field">
                      <label>Company Email Address</label>
                      <div className="auth-input-icon">
                        <Mail size={18} className="auth-input-icon-svg" />
                        <input
                          className={`auth-input ${errors.email || emailCheck.status === 'invalid' ? 'error' : ''} ${emailCheck.status === 'valid' ? 'valid' : ''}`}
                          type="email"
                          value={form.email}
                          onChange={set('email')}
                          placeholder="Enter company email"
                        />
                        {emailCheck.status === 'checking' && (
                          <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)' }}>
                            <span className="spinner" style={{ width: 14, height: 14, borderWidth: 2, display: 'inline-block' }} />
                          </span>
                        )}
                        {emailCheck.status === 'valid' && (
                          <CheckCircle size={16} color="#16a34a" style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)' }} />
                        )}
                      </div>
                      {errors.email && <div className="auth-field-error">{errors.email}</div>}
                      {!errors.email && emailCheck.status === 'invalid' && (
                        <div className="auth-field-error">{emailCheck.msg}</div>
                      )}
                    </div>

                    <div className="auth-field">
                      <label>Phone Number</label>
                      <div className="auth-input-icon">
                        <span style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: '#94a3b8' }}>📱</span>
                        <input className="auth-input" value={form.phone} onChange={set('phone')} placeholder="Enter phone number" style={{ paddingLeft: 42 }} />
                      </div>
                    </div>

                    {/* ===== PHONE OTP VERIFICATION (sign-in phone) ===== */}
                    {firebaseEnabled ? (
                      <div style={{ border: `1px solid ${otpVerified ? '#bbf7d0' : '#e2e8f0'}`, background: otpVerified ? '#f0fdf4' : '#f8fafc', borderRadius: 12, padding: 14 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 700, color: otpVerified ? '#166534' : '#334155' }}>
                          {otpVerified ? <CheckCircle size={15} /> : <Phone size={15} />}
                          {otpVerified ? 'Phone verified — you can sign in with SMS code' : 'Sign-in phone (SMS verification)'}
                        </label>
                        {!otpVerified && (
                          otpStage === 'phone' ? (
                            <>
                              <div className="auth-input-icon" style={{ marginTop: 9 }}>
                                <Phone size={18} className="auth-input-icon-svg" />
                                <input className="auth-input" type="tel" value={otpPhone} onChange={e => setOtpPhone(e.target.value)} placeholder="+93700123456" />
                              </div>
                              <button type="button" onClick={handleSendOtp} disabled={otpBusy}
                                style={{ marginTop: 9, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '9px 12px', borderRadius: 9, border: 'none', background: '#4f46e5', color: '#fff', fontWeight: 700, fontSize: 13, cursor: otpBusy ? 'default' : 'pointer' }}>
                                {otpBusy ? <><span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Sending...</> : <><MessageSquare size={14} /> Send verification code</>}
                              </button>
                            </>
                          ) : (
                            <>
                              <input className="auth-input" inputMode="numeric" maxLength={6}
                                value={otpCode}
                                onChange={e => setOtpCode(e.target.value.replace(/\D/g, ''))}
                                placeholder="● ● ● ● ● ●"
                                style={{ textAlign: 'center', letterSpacing: 8, fontSize: 18, fontWeight: 700, marginTop: 9 }}
                              />
                              <div style={{ display: 'flex', gap: 8, marginTop: 9 }}>
                                <button type="button" onClick={() => { setOtpStage('phone'); setOtpCode(''); setConfirmResult(null); }}
                                  style={{ flex: 1, padding: '9px 10px', borderRadius: 9, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' }}>
                                  Change
                                </button>
                                <button type="button" onClick={handleVerifyRegisterOtp} disabled={otpBusy}
                                  style={{ flex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '9px 10px', borderRadius: 9, border: 'none', background: '#16a34a', color: '#fff', fontWeight: 700, fontSize: 12.5, cursor: otpBusy ? 'default' : 'pointer' }}>
                                  {otpBusy ? 'Verifying...' : 'Verify phone'}
                                </button>
                              </div>
                              <button type="button" onClick={handleSendOtp} disabled={resendIn > 0 || otpBusy}
                                style={{ marginTop: 8, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, background: 'none', border: 'none', color: resendIn > 0 ? '#94a3b8' : '#4f46e5', fontWeight: 600, fontSize: 12, cursor: resendIn > 0 ? 'default' : 'pointer' }}>
                                <RefreshCw size={11} /> {resendIn > 0 ? `Resend code in ${resendIn}s` : 'Resend code'}
                              </button>
                            </>
                          )
                        )}
                        <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 7 }}>Optional — lets you log in with an SMS code instead of a password</div>
                      </div>
                    ) : null}

                    <div className="auth-row">
                      <div className="auth-field">
                        <label>First Name</label>
                        <div className="auth-input-icon">
                          <User size={18} className="auth-input-icon-svg" />
                          <input className="auth-input" value={form.firstName} onChange={set('firstName')} placeholder="John" />
                        </div>
                      </div>
                      <div className="auth-field">
                        <label>Last Name</label>
                        <div className="auth-input-icon">
                          <User size={18} className="auth-input-icon-svg" />
                          <input className="auth-input" value={form.lastName} onChange={set('lastName')} placeholder="Doe" />
                        </div>
                      </div>
                    </div>

                    <div className="auth-field">
                      <label>Username <span className="req">*</span></label>
                      <div className="auth-input-icon">
                        <User size={18} className="auth-input-icon-svg" />
                        <input className={`auth-input ${errors.username ? 'error' : ''}`} value={form.username} onChange={set('username')} placeholder="Choose a username" />
                      </div>
                      {errors.username && <div className="auth-field-error">{errors.username}</div>}
                    </div>

                    <div className="auth-field">
                      <label>Create Password <span className="req">*</span></label>
                      <div className="auth-input-icon auth-input-with-toggle">
                        <Lock size={18} className="auth-input-icon-svg" />
                        <input className={`auth-input ${errors.password ? 'error' : ''}`} type={showPw ? 'text' : 'password'} value={form.password} onChange={set('password')} placeholder="Enter password" minLength="6" />
                        <button type="button" className="auth-pw-toggle" onClick={() => setShowPw(v => !v)}>
                          {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      {errors.password && <div className="auth-field-error">{errors.password}</div>}
                      {form.password && (
                        <div>
                          <div className="auth-pw-strength">
                            {[1, 2, 3, 4].map(i => (
                              <div key={i} className={`auth-pw-strength-bar ${i <= pwStrength.level ? pwStrength.class : ''}`} />
                            ))}
                          </div>
                          <div className={`auth-pw-strength-label ${pwStrength.class}`}>{pwStrength.label}</div>
                        </div>
                      )}
                    </div>

                    <div className="auth-field">
                      <label>Confirm Password <span className="req">*</span></label>
                      <div className="auth-input-icon auth-input-with-toggle">
                        <Lock size={18} className="auth-input-icon-svg" />
                        <input className={`auth-input ${errors.confirmPassword ? 'error' : ''}`} type={showPwConfirm ? 'text' : 'password'} value={form.confirmPassword} onChange={set('confirmPassword')} placeholder="Confirm your password" />
                        <button type="button" className="auth-pw-toggle" onClick={() => setShowPwConfirm(v => !v)}>
                          {showPwConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      {errors.confirmPassword && <div className="auth-field-error">{errors.confirmPassword}</div>}
                    </div>

                    <label className="auth-terms">
                      <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} />
                      <span>I agree to the <a href="/terms-of-service" target="_blank">Terms of Service</a> and <a href="/privacy-policy" target="_blank">Privacy Policy</a></span>
                    </label>

                    <button className="auth-submit auth-submit-primary" type="submit">
                      Create Company <ArrowRight size={16} />
                    </button>
                  </form>
                </div>
              </>
            )}

            {/* ===== STEP 2: PLAN SELECTION ===== */}
            {step === 2 && (
              <>
                <div className="auth-card-header">
                  <div className="auth-form-logo">
                    {platformLogo ? (
                      <img src={fileUrl(platformLogo)} alt="Platform Logo" className="auth-form-logo-img" style={{ objectFit: 'contain' }} />
                    ) : (
                      <img src="/favicon.svg" alt="LedgerPro" className="auth-form-logo-img" />
                    )}
                  </div>
                  <h2>Choose your plan</h2>
                  <p>Select the best plan for your business</p>
                </div>
                <div className="auth-card-body">
                  <div className="auth-billing-toggle">
                    <div className="auth-billing-toggle-inner">
                      <button type="button" className={`auth-billing-btn ${billingCycle === 'monthly' ? 'active' : ''}`} onClick={() => setBillingCycle('monthly')}>Monthly</button>
                      <button type="button" className={`auth-billing-btn ${billingCycle === 'yearly' ? 'active' : ''}`} onClick={() => setBillingCycle('yearly')}>Yearly <span className="auth-billing-save">Save 17%</span></button>
                    </div>
                  </div>

                  {plans.length === 0 ? (
                    <div style={{ padding: '24px', textAlign: 'center', color: '#64748b', fontSize: 13, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10 }}>
                      No subscription plans are available right now. Please contact support to create your company.
                    </div>
                  ) : (
                    <>
                      <div className="auth-plan-grid">
                        {plans.map(plan => {
                          const isSelected = selectedPlan?.id === plan.id;
                          const isTrial = plan.slug === 'free-trial';
                          const perMonth = billingCycle === 'yearly' ? plan.price_yearly / 12 : plan.price_monthly;
                          const price = billingCycle === 'yearly' ? plan.price_yearly : plan.price_monthly;
                          const features = (() => { try { return JSON.parse(plan.features || '[]'); } catch { return []; } })();
                          return (
                            <div key={plan.id} className={`auth-plan-card ${isSelected ? 'selected' : ''}`} onClick={() => { setSelectedPlan(plan); if (errors.plan) setErrors({ ...errors, plan: null }); }}>
                              <div className="auth-plan-check">✓</div>
                              {isTrial && (
                                <div style={{ position: 'absolute', top: 10, right: 10, background: '#16a34a', color: '#fff', fontSize: 9, fontWeight: 800, letterSpacing: 0.5, padding: '3px 8px', borderRadius: 999, textTransform: 'uppercase' }}>
                                  14-day trial
                                </div>
                              )}
                              <div className="auth-plan-name">{plan.name}</div>
                              <div className="auth-plan-price">{isTrial ? 'Free' : fmt(perMonth)}<span>{isTrial ? '' : '/mo'}</span></div>
                              {!isTrial && billingCycle === 'yearly' && <div className="auth-plan-yearly">Billed {fmt(price)}/yr</div>}
                              {isTrial && <div className="auth-plan-yearly">Full access for 14 days — no credit card</div>}
                              <div className="auth-plan-limits">{plan.max_users} users · {plan.max_invoices_month === -1 ? 'Unlimited' : plan.max_invoices_month} invoices/mo</div>
                              {features.length > 0 && (
                                <ul className="auth-plan-features">
                                  {features.slice(0, 3).map((f, i) => <li key={i}>{f}</li>)}
                                </ul>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {errors.plan && (
                        <div style={{ marginTop: 12, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 8, fontSize: 13, fontWeight: 600 }}>
                          ⚠ {errors.plan} — selecting a plan is required
                        </div>
                      )}

                      {selectedPlan && (
                        <div className="auth-trial-notice">
                          <div className="auth-trial-notice-icon">14</div>
                          <div className="auth-trial-notice-text">
                            <strong>14-day free trial</strong> — Your subscription will be pending admin confirmation. You'll have full access to all <strong>{selectedPlan.name}</strong> features for 14 days.
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  <div className="auth-actions">
                    <button className="btn-back-outline" type="button" onClick={() => setStep(1)}>
                      <ArrowLeft size={14} /> Back
                    </button>
                    <button className="auth-submit auth-submit-primary" type="button" onClick={handlePlanSubmit} disabled={loading || !selectedPlan}>
                      {loading ? (
                        <><span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> Creating...</>
                      ) : !selectedPlan ? (
                        <>Select a Plan to Continue</>
                      ) : (
                        <>Create Company & Start Free Trial <ArrowRight size={16} /></>
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="auth-footer">
            Already have a company? <Link to="/login">Sign In</Link>
          </div>
        </div>

        {/* Footer */}
        <div className="auth-page-footer">
          <span>© 2026 Ledger Pro</span>
          <span className="sep">·</span>
          <a href="/privacy-policy">Privacy Policy</a>
          <span className="sep">·</span>
          <a href="/terms-of-service">Terms of Service</a>
          <span className="sep">·</span>
          <a href="/security">Security</a>
          <span className="sep">·</span>
          <a href="/plans">View Plans</a>
          <span className="sep">·</span>
          <a href="/contact">Contact</a>
        </div>
      </div>
    </div>
  );
}
